import type {
  PaymentMethod,
  PaymentRequestWallet,
  Stripe,
} from "@stripe/stripe-js";

export type PreferredExpressWallet = "apple_pay" | "google_pay";

/** Merchant country for Payment Request (Stripe account country). Default GB for this product. */
export function paymentRequestCountryForCurrency(currency: string): string {
  const c = currency.trim().toLowerCase();
  if (c === "eur") return "DE";
  if (c === "usd") return "US";
  return "GB";
}

function disableWalletsForPreferred(
  preferred: PreferredExpressWallet,
): PaymentRequestWallet[] {
  if (preferred === "apple_pay") {
    return ["googlePay", "link", "browserCard"];
  }
  return ["applePay", "link", "browserCard"];
}

export interface RunWalletPaymentRequestParams {
  stripe: Stripe;
  /** Amount in minor units — must match server PaymentIntent / invoice. */
  amountMinor: number;
  currency: string;
  label: string;
  preferredWallet: PreferredExpressWallet;
  /** Confirm on your API + Stripe; do not call PaymentRequest.complete yourself. */
  onPaymentMethod: (paymentMethod: PaymentMethod) => Promise<void>;
}

/**
 * Opens the native Apple Pay or Google Pay sheet (no Stripe Checkout redirect).
 * Uses Stripe.js Payment Request with wallets explicitly filtered so the sheet matches the button.
 */
export function runWalletPaymentRequest(
  params: RunWalletPaymentRequestParams,
): Promise<void> {
  const {
    stripe,
    amountMinor,
    currency,
    label,
    preferredWallet,
    onPaymentMethod,
  } = params;

  const country = paymentRequestCountryForCurrency(currency);

  const paymentRequest = stripe.paymentRequest({
    country,
    currency: currency.trim().toLowerCase(),
    total: {
      label: label.slice(0, 120),
      amount: amountMinor,
    },
    requestPayerName: true,
    requestPayerEmail: true,
    disableWallets: disableWalletsForPreferred(preferredWallet),
  });

  return new Promise<void>((resolve, reject) => {
    let settled = false;

    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      paymentRequest.off("paymentmethod");
      paymentRequest.off("cancel");
      fn();
    };

    void paymentRequest.canMakePayment().then((result) => {
      if (!result) {
        reject(
          new Error(
            preferredWallet === "apple_pay"
              ? "Apple Pay is not available on this device or browser."
              : "Google Pay is not available on this device or browser.",
          ),
        );
        return;
      }

      if (preferredWallet === "apple_pay" && !result.applePay) {
        reject(new Error("Apple Pay is not available here."));
        return;
      }
      if (preferredWallet === "google_pay" && !result.googlePay) {
        reject(new Error("Google Pay is not available here."));
        return;
      }

      paymentRequest.on("paymentmethod", async (ev) => {
        try {
          await onPaymentMethod(ev.paymentMethod);
          ev.complete("success");
          finish(() => resolve());
        } catch (err) {
          ev.complete("fail");
          finish(() => reject(err instanceof Error ? err : new Error(String(err))));
        }
      });

      paymentRequest.on("cancel", () => {
        finish(() => reject(new Error("cancelled")));
      });

      paymentRequest.show();
    });
  });
}
